import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Instagram, Clock, Accessibility, Train, Car, Navigation, MessageCircle, Facebook } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SITE } from "@/lib/site";

const TRAVEL_ICONS = [Train, Accessibility, Car];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setLoading(true);
    // Phase 1: local acknowledgement. Wire to backend in Phase 2.
    setTimeout(() => {
      setLoading(false);
      setForm({ name: "", email: "", message: "" });
      toast.success("Thank you — we'll be in touch within 3 working days.");
    }, 700);
  };

  return (
    <div data-testid="page-contact" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="overline">Contact</div>
            <h1 className="font-display text-5xl sm:text-7xl mt-4 tracking-tight text-balance">
              Let's <span className="italic font-light">talk hair.</span>
            </h1>
            <p className="mt-6 text-[hsl(var(--onyx)/0.65)] max-w-md leading-relaxed">
              For consultations, courses, collaborations or press — send us a
              message here, drop us an email, or DM us on Instagram, and we'll
              be in touch within 3 working days.
            </p>

            <div className="mt-10 space-y-5">
              {[
                {
                  i: MapPin,
                  l: "Salon · The Wee Nook",
                  v: SITE.addressLines ? SITE.addressLines.join(", ") : SITE.addressLine,
                },
                { i: Mail, l: "Email", v: SITE.email, h: `mailto:${SITE.email}` },
                {
                  i: Instagram,
                  l: "Instagram · DM us",
                  v: SITE.instagramHandle,
                  h: SITE.instagramUrl,
                },
                {
                  i: Facebook,
                  l: "Facebook · Message us",
                  v: SITE.facebookHandle,
                  h: SITE.facebookUrl,
                },
              ].map(({ i: Icon, l, v, h }) => (
                <div key={l} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center">
                    <Icon size={16} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                      {l}
                    </div>
                    {h ? (
                      <a
                        href={h}
                        target={h.startsWith("http") ? "_blank" : undefined}
                        rel="noreferrer"
                        className="font-display text-lg"
                      >
                        {v}
                      </a>
                    ) : (
                      <div className="font-display text-lg">{v}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {SITE.travelNotes && SITE.travelNotes.length > 0 && (
              <div className="mt-8 rounded-[1.5rem] bg-blush p-6 space-y-3" data-testid="travel-notes">
                <div className="overline">Getting here</div>
                {SITE.travelNotes.map((note, i) => {
                  const Icon = TRAVEL_ICONS[i] || MapPin;
                  return (
                    <div key={i} className="flex items-start gap-3 text-sm text-[hsl(var(--onyx)/0.8)]">
                      <Icon size={16} className="mt-0.5 text-[hsl(var(--gold))] shrink-0" />
                      <span>{note}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {SITE.hours && (
              <div className="mt-6 rounded-[1.5rem] bg-white border border-[hsl(var(--onyx)/0.08)] p-6" data-testid="opening-hours">
                <div className="overline flex items-center gap-2">
                  <Clock size={12} /> Opening hours
                </div>
                <ul className="mt-3 space-y-2 text-sm">
                  {SITE.hours.map((h) => (
                    <li key={h.day} className="flex justify-between gap-4">
                      <span className="text-[hsl(var(--onyx)/0.6)]">{h.day}</span>
                      <span className="font-medium text-[hsl(var(--onyx))]">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {SITE.bookingNote && (
              <p className="mt-6 text-xs text-[hsl(var(--onyx)/0.6)] leading-relaxed">
                {SITE.bookingNote}
              </p>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-book-cta"
                className="btn-pill btn-primary"
              >
                Book online <ArrowUpRight size={16} />
              </a>
              {SITE.mapDirectionsUrl && (
                <a
                  href={SITE.mapDirectionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-directions"
                  className="btn-pill btn-outline"
                >
                  <Navigation size={14} /> Get directions
                </a>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-[2rem] bg-white border border-[hsl(var(--onyx)/0.08)] p-8 sm:p-10"
            >
              <div className="grid gap-6">
                <div>
                  <Label htmlFor="c-name">Name</Label>
                  <Input
                    id="c-name"
                    data-testid="contact-name"
                    className="mt-2 h-12 rounded-2xl"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label htmlFor="c-email">Email</Label>
                  <Input
                    id="c-email"
                    type="email"
                    data-testid="contact-email"
                    className="mt-2 h-12 rounded-2xl"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="c-message">Message</Label>
                  <Textarea
                    id="c-message"
                    rows={6}
                    data-testid="contact-message"
                    className="mt-2 rounded-2xl"
                    value={form.message}
                    onChange={(e) =>
                      setForm({ ...form, message: e.target.value })
                    }
                    placeholder="Tell us about your hair, question or enquiry..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-pill btn-primary self-start disabled:opacity-70"
                >
                  {loading ? "Sending..." : "Send message"}{" "}
                  <ArrowUpRight size={16} />
                </button>
              </div>
            </form>

            <div className="mt-8 rounded-[2rem] overflow-hidden border border-[hsl(var(--onyx)/0.08)] aspect-[16/9]">
              <iframe
                title="Studio Map"
                src={SITE.mapEmbed}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
